<template>
  <main id="main-content">
    <div class="body-content sessions-container pt-0">
      <div class="full-width-content--small">
        <!-- Sesión destacada: en directo o próxima (solo roles no-admin) -->
        <!-- El gate por enrollment se eliminó a propósito: la card destacada ocupa la parte superior y,
             al aparecer más tarde, empujaba hacia abajo la lista ya pintada. El botón "Unirse" ya está
             condicionado por su cuenta más abajo. -->
        <div class="session-featured" v-if="featured_classroom">
          <div
            class="session-box"
            :class="sessionState(featured_classroom) && 'session-state-' + sessionState(featured_classroom)"
          >
            <div class="d-flex align-items-center session-box-first-line">
              <template v-if="sessionState(featured_classroom) === 'not-started'">
                <div class="sessions-banner-next-session flex-grow-1">
                  <div class="icon-container icon-container-blue mr-3">
                    <i class="isax isax-calendar-tick"/>
                  </div>
                  <span>{{ $t("sessions.comingsoon") }}</span>
                </div>
                <div class="sessions-banner">
                  <span>{{ $t("sessions.notstarted") }}</span>
                </div>
              </template>
              <template v-else-if="sessionState(featured_classroom) === 'in-progress'">
                <div class="sessions-banner-in-progress">
                  <span class="pulse-dot"></span>
                  <span>{{ $t("sessions.inprogress") }}</span>
                </div>
              </template>
            </div>
            <div class="d-flex flex-column flex-lg-row session-featured-info-lines">
              <div class="session-date">
                <i class="isax isax-calendar-1"/>
                <span class="session-start-date flex-grow-1">
                  <span>{{ featured_classroom.start | moment("D MMM YYYY") }}</span>
                  <span>{{ featured_classroom.start | moment("HH:mma") }}</span>
                </span>
              </div>
              <div class="session-duration">
                <i class="isax isax-timer-1"/>
                <span>{{ $t("recordings.duration") }}: {{ classDuration(featured_classroom) }} {{ $t("sessions.min") }}</span>
              </div>
              <div class="session-host" v-if="featured_classroom.host && featured_classroom.host.fullName">
                <i class="isax isax-profile-circle"/>
                <span>{{ $t("sessions.host") }}: {{ featured_classroom.host.fullName }}</span>
              </div>
            </div>
            <div class="d-flex flex-column flex-lg-row session-featured-info-lines">
              <div v-if="featured_classroom.description && featured_classroom.description.trim()" class="mt-1">
                <span>{{ $t("sessions.notes") }}</span>
                <span>{{ featured_classroom.description }}</span>
              </div>
              <div class="session-controls d-flex ml-lg-auto align-self-center align-self-lg-auto mt-2 mt-lg-0">
                <button v-if="(user_roles.indexOf('ROLE_PRESENTER') !== -1) && profiles.indexOf('argos') !== -1 && isOngoingBeforeX(featured_classroom, 30)"
                        class="page-controls__primary-action button secondary button-panic mr-1" @click="showIncidentPanel(featured_classroom)" :title="$t('sessions.panicbutton.title')">
                  <phone-alert-outline :size="20"/>
                  <span>{{ $t("sessions.panicbutton.text") }}</span>
                </button>
                <button class="page-controls__primary-action button primary short" v-if="!hasEnded(featured_classroom)" v-show="featured_classroom.enrollment && featured_classroom.enrollment.launchUrl" @click="launchUrl(featured_classroom.enrollment.launchUrl, '_blank')">
                  <span>{{ $t("sessions.joinclass") }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <!-- Sesiones en reproducción: carrusel de sesiones, para estudiantes -->
        <div class="session-playing" v-if="in_progress_recordings.length">
          <div class="session-playing-box">
            <div class="session-playing-title">
              <div class="icon-container icon-container-orange mr-3">
                <i class="isax isax-clock"/>
              </div>
              <div>
                <div class="banner-title">{{ $t("sessions.keepwatching") }}</div>
                <div class="banner-subtitle" v-html="$t('sessions.pendingrecordings', { count: pending_recordings_count })"></div>
              </div>
            </div>
            <carousel
              class="session-playing-carousel"
              :per-page-custom="[[0, 1], [768, 2], [992, 3]]"
              :scroll-per-page="false"
              :pagination-enabled="true"
              :navigation-enabled="true"
              :navigation-click-target-size="0"
              navigation-prev-label="<i class='isax isax-arrow-left-2'></i>"
              navigation-next-label="<i class='isax isax-arrow-right-3'></i>"
            >
              <slide v-for="recording in in_progress_recordings" :key="recording.id">
                <InProgressRecordingCard
                  :recording="recording"
                  @play-recording="handlePlayRecording"
                  @removed="handleRemoveInProgress"
                  @viewed="handleViewedInProgress"
                />
              </slide>
            </carousel>
          </div>
        </div>
        <!-- Subtítulo y controles del listado (nuevo diseño) -->
        <div class="d-flex align-items-center sessions-list-header">
          <h2 class="section-title align-self-center m-0">{{ $t("sessions.list") }}</h2>
          <div class="ml-auto sessions-list-actions" v-if="isAdmin || user_roles.indexOf('ROLE_EVALUATOR') !== -1">
            <button class="page-controls__primary-action button primary" v-if="user_roles.indexOf('ROLE_EVALUATOR') !== -1" v-b-toggle.sidebar-no-header="'sidebar-gradeattendance'">
              <span>{{ $t("sessions.gradeattendance") }}</span>
            </button>
            <button class="page-controls__primary-action button primary" v-if="isAdmin" v-b-toggle.sidebar-no-header="'sidebar-new'">
              <span>{{ $t("sessions.create") }}</span>
            </button>
            <button class="page-controls__primary-action button secondary" v-if="isAdmin" v-b-toggle.sidebar-no-header="'sidebar-reuse'">
              <span>{{ $t("sessions.reuse") }}</span>
            </button>
            <button class="page-controls__primary-action button secondary" v-if="isAdmin" @click="clearCache()">
              <span>{{ $t("sessions.clearcache") }}</span>
            </button>
          </div>
        </div>
        <div class="sessions-box">
          <div class="sessions-box-header">
            <div class="d-flex flex-column flex-md-row">
              <div class="d-flex align-items-center">
                <!-- EdF: El aviso queda fijo por ahora: se le quitó la X y el estado que la ocultaba.
                     Si se quiere recuperar, vuelve un `v-if` sobre una bandera y el botón de cierre,
                     que tiene sus estilos en `.sessions-banner__close` de _custom.scss. -->
                <div class="sessions-banner sessions-info-banner">
                  <i class="isax isax-info-circle mr-2"></i>
                  <span class="mr-2">{{ $t("sessions.periodicupdate") }}</span>
                </div>
              </div>
              <div class="d-flex ml-md-auto mt-2 mt-md-auto">
                <b-dropdown right variant=" dropdown-filter__select button button-select dropdown pr-0 mr-3" no-caret>
                  <template #button-content>
                    <div class="dropdown-filter select-wrapper">
                      <i class="isax isax-arrow-3" />
                      <span class="dropdown-filter__select-label"><strong>{{ $t("sessions.orderby") }}</strong>: {{ sort_order === 'newest' ? $t("sessions.orderbynewest") : $t("sessions.orderbyoldest") }}</span>
                    </div>
                  </template>
                  <b-dropdown-item @click="sort_order = 'oldest'">{{ $t("sessions.orderbyoldest") }}</b-dropdown-item>
                  <b-dropdown-item @click="sort_order = 'newest'">{{ $t("sessions.orderbynewest") }}</b-dropdown-item>
                </b-dropdown>
                <b-dropdown right variant=" dropdown-filter__select button button-select dropdown pr-0" no-caret>
                  <template #button-content>
                    <div class="dropdown-filter select-wrapper">
                      <i class="isax isax-filter" />
                      <span class="dropdown-filter__select-label"><strong>{{ $t("sessions.showfilter") }}</strong>: {{ selected_filter }}</span>
                    </div>
                  </template>
                  <b-dropdown-item @click="allSessions()">{{ $t("sessions.showfilterall") }}</b-dropdown-item>
                  <b-dropdown-item v-if="!isPureAdmin" @click="viewedSessions()">{{ $t("sessions.showfilterviewed") }}</b-dropdown-item>
                  <b-dropdown-item v-if="!isPureAdmin" @click="notViewedSessions()">{{ $t("sessions.showfilternotviewed") }}</b-dropdown-item>
                  <b-dropdown-item v-if="!isPureAdmin" @click="favoriteSessions()">{{ $t("sessions.showfilterfavorite") }}</b-dropdown-item>
                  <!-- EdF: nextSessions/pastSessions ocultos del listado (se conserva el código por si se reenganchan en el futuro) -->
                  <!-- <b-dropdown-item @click="nextSessions()">{{ $t("sessions.allnext") }}</b-dropdown-item> -->
                  <!-- <b-dropdown-item @click="pastSessions()">{{ $t("sessions.allpast") }}</b-dropdown-item> -->
                  <b-dropdown-item v-if="isAdmin" @click="intervalSessions()">{{ $t("sessions.interval") }}</b-dropdown-item>
                  <b-dropdown-item v-if="isAdmin" @click="searchByHostSessions()">{{ $t("sessions.searchbyhost") }}</b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
            <div class="d-flex justify-content-end sessions-filter-container">
              <form class="date-range" v-if="selected_filter===$t('sessions.interval')">
                <div class="picker-input date">
                  <p>{{ $t("recordings.datefrom") }}</p>
                  <b-form-datepicker
                    id="start-date" type="text"
                    v-model="filter_start_date"
                    :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                    locale="es"
                    @input="getUserGroupsClassrooms()">
                  </b-form-datepicker>
                </div>
                <div class="picker-input date mr-0">
                  <p>{{ $t("recordings.dateto") }}</p>
                  <b-form-datepicker
                    id="end-date" type="text"
                    v-model="filter_end_date"
                    :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                    locale="es"
                    @input="getUserGroupsClassrooms()">
                  </b-form-datepicker>
                </div>
              </form>
              <form class="date-range" v-if="selected_filter===$t('sessions.searchbyhost')">
                <div class="picker-host mr-0">
                  <select2-host
                    class="custom-select"
                    :name="'filter-host'"
                    callback="/api/v1/users/"
                    v-model="filter_host"
                    v-bind:placeholder="$t('sessions.searchbyhostplaceholder')"
                    @input="getUserGroupsClassrooms()">
                  </select2-host>
                </div>
              </form>
            </div>
          </div>
          <ul class="item-list session-list" v-show="!loading">
            <li v-for="item in sorted_session_items" v-bind:key="item.key">
              <SessionCard
                v-if="item.type === 'classroom'"
                :classroom="item.classroom"
                :user_roles="user_roles"
                :profiles="profiles"
                :user_groups="user_groups"
                @show-incident="showIncidentPanel"
                @confirm-delete="confirmDelete"
                @play-recording="handlePlayRecording"
                @recording-viewed="handleRemoveInProgress"
              />
              <ConnectRecordingCard
                v-else-if="item.type === 'recording'"
                :recording="item.recording"
                :classroom="item.classroom"
                @play-recording="handlePlayRecording"
              />
            </li>
          </ul>
          <div v-if="!loading && !sorted_session_items.length" class="no-content no-results">
            <span class="image pb-3">
              <img class="site-logo" src="/static/img/miroodles_sticker.png" />
            </span>
            <span class="no-content-title">{{ $t("sessions.nonextsessions") }}</span>
            <span class="no-content-title mt-3">{{ $t("sessions.nonextsessions2") }}</span>
          </div>
        </div>
      </div>
    </div>
    <ReuseSessionSidebar
      @reuse="reuseClassroom">
    </ReuseSessionSidebar>
    <GradeAttendanceSidebar
      :groups="user_groups">
    </GradeAttendanceSidebar>
    <EditClassroomSidebar
      :groups="user_groups"
      @refresh="getUserGroupsClassrooms()">
    </EditClassroomSidebar>
    <EditClassroomSidebar
      id="sidebar-edit-reuse"
      :classroom="reuse_classroom"
      :groups="user_groups"
      @refresh="getUserGroupsClassrooms()">
    </EditClassroomSidebar>
    <vue-confirm-dialog class="pv-confirm"></vue-confirm-dialog>
    <IncidentPanel :classroom="incident_classroom"></IncidentPanel>
    <RecordingVideoPlayer :player_url="player_url" :captions_url="player_captions_url" :chat="chat" :title="player_title" player_id="" :recording="current_recording" @recording-progress="handleRecordingProgress"></RecordingVideoPlayer>
  </main>
</template>

<script>
import PhoneAlertOutline from 'vue-material-design-icons/PhoneAlertOutline.vue'
import jwtMixin from '../../mixins/jwtMixin'
import utilitiesMixin from '../../mixins/utilitiesMixin'
import ReuseSessionSidebar from './SessionsModals/ReuseSessionSidebar'
import EditClassroomSidebar from './SessionsModals/EditClassroomSidebar'
import GradeAttendanceSidebar from './SessionsModals/GradeAttendanceSidebar'
import IncidentPanel from './SessionsModals/IncidentPanel'
import Sidebar from '../../components/Lti/Sidebar'
import RecordingVideoPlayer from '../Player/RecordingVideoPlayer'
import SessionCard from './SessionsComponents/SessionCard'
import ConnectRecordingCard from './SessionsComponents/ConnectRecordingCard'
import InProgressRecordingCard from './SessionsComponents/InProgressRecordingCard'
import Select2Host from '../Select2/Select2Host'
import { Carousel, Slide } from 'vue-carousel'

export default {
  name: 'Sessions',
  components: {Sidebar, PhoneAlertOutline, EditClassroomSidebar, ReuseSessionSidebar, GradeAttendanceSidebar, IncidentPanel, RecordingVideoPlayer, SessionCard, ConnectRecordingCard, InProgressRecordingCard, Select2Host, Carousel, Slide},
  mixins: [jwtMixin, utilitiesMixin],
  created () {
    this.$i18n.locale = this.jwtLanguage()
    this.initUserClassrooms()
  },
  data () {
    return {
      loading: false,
      // EdF: Las filas de grabaciones CONNECT se retienen hasta que han respondido todas las llamadas
      // de grabaciones, para que entren en un único reflow en lugar de irse insertando una a una
      recordings_loaded: false,
      user_groups: [],
      user_roles: [],
      user_classrooms: [],
      selected_filter: this.$i18n.t('sessions.showfilterall'),
      search_show: false,
      filter_start_date: null,
      filter_end_date: null,
      filter_host: null,
      filter_name: null,
      waiting_filter_name: false,
      reuse_classroom: null,
      incident_classroom: null,
      profiles: [],
      sort_order: 'oldest',
      visual_filter: null,
      data_loaded: false,
      pending_recordings_count: 0,
      in_progress_recordings: [],
      player_url: null,
      player_title: null,
      player_captions_url: null,
      chat: null,
      current_recording: null
    }
  },
  computed: {
    sorted_user_classrooms: function () {
      let vueComponent = this
      const sorted = vueComponent.user_classrooms.slice().sort((a, b) => {
        const diff = window.moment(a.start).diff(window.moment(b.start))
        return vueComponent.sort_order === 'newest' ? -diff : diff
      })
      // CMM: Se eliminan posibles duplicados
      return vueComponent.getUnique(sorted, 'id')
    },
    filtered_user_classrooms: function () {
      // EdF: El filtro "Vistas / No vistas" aplica sobre el estado de visualización de la grabación
      // (main_recording.viewed), no sobre la asistencia en directo. Las sesiones sin grabación no
      // tienen estado de visualización, por lo que quedan fuera de ambos filtros.
      if (this.visual_filter === 'viewed') {
        return this.sorted_user_classrooms.filter(c => c.main_recording && c.main_recording.viewed)
      }
      if (this.visual_filter === 'not-viewed') {
        return this.sorted_user_classrooms.filter(c => c.main_recording && !c.main_recording.viewed)
      }
      return this.sorted_user_classrooms
    },
    showConnectRecordings: function () {
      // EdF: Las grabaciones CONNECT no tienen marca de vista ni favorito, así que quedan fuera de
      // esos filtros. Sí participan en la ordenación por fecha del resto de vistas.
      return this.visual_filter === null && this.selected_filter !== this.$i18n.t('sessions.showfilterfavorite')
    },
    sorted_session_items: function () {
      let vueComponent = this
      const items = []
      vueComponent.filtered_user_classrooms.forEach(c => {
        items.push({ key: 'c-' + c.id, type: 'classroom', classroom: c, date: c.start })
        // EdF: Estas filas no existen hasta que responde el GET de grabaciones de su clase y, al
        // ordenarse por fecha, se insertarían en medio de la lista ya pintada. Se retienen hasta
        // que están todas para que aparezcan de una vez.
        if (vueComponent.showConnectRecordings && vueComponent.recordings_loaded) {
          (c.connect_recordings || []).forEach(r => {
            items.push({ key: 'r-' + r.id, type: 'recording', classroom: c, recording: r, date: r.startTime || c.start })
          })
        }
      })
      return items.sort((a, b) => {
        const diff = window.moment(a.date).diff(window.moment(b.date))
        return vueComponent.sort_order === 'newest' ? -diff : diff
      })
    },
    featured_classroom: function () {
      // EdF: La sesión destacada no se muestra a administradores puros (ROLE_ADMIN sin ningún otro rol)
      // eslint-disable-next-line camelcase
      const userClassroomsOrderedByStartTime = this.user_classrooms.slice().reverse() // invertimos el orden para empezar por las más recientes
      if (this.isPureAdmin) return null
      const inProgress = userClassroomsOrderedByStartTime.find(c => this.sessionState(c) === 'in-progress')
      if (inProgress) return inProgress
      return userClassroomsOrderedByStartTime.find(c => this.sessionState(c) === 'not-started') || null
    },
    isAdmin: function () {
      return this.user_roles.indexOf('ROLE_ADMIN') !== -1
    },
    isPureAdmin: function () {
      // EdF: Administrador puro = ROLE_ADMIN sin ningún otro rol. Un admin con otro rol adicional ve todos los filtros
      return this.isAdmin && this.user_roles.every(r => r === 'ROLE_ADMIN')
    }
  },
  methods: {
    initUserClassrooms () {
      let vueComponent = this
      this.loading = true

      const licenceId = this.jwtConsumerId()
      const site = this.jwtContextSite()
      this.user_roles = this.jwtLoggedUserRoles()
      this.profiles = this.jwtProfiles()

      // CMM: Se recuperan los grupos para el usuario conectado. Los administradores tiene acceso a todos los grupos
      if (this.isAdmin) {
        window.axios.get('licences/' + licenceId + '/site/' + site + '/unirgroups').then((response) => {
          vueComponent.user_groups = response.data
          // CMM: Se recuperan las clases virtuales para cada grupo
          vueComponent.allSessions()
          // EdF: Grabaciones en curso, acotadas a los grupos del contexto (requiere user_groups ya cargado)
          vueComponent.loadInProgressRecordings()
        })
      } else {
        window.axios.get('licences/' + licenceId + '/site/' + site + '/currentuser/unirgroups').then((response) => {
          vueComponent.user_groups = response.data
          // CMM: Se recuperan las clases virtuales para cada grupo
          vueComponent.allSessions()
          // EdF: Grabaciones en curso, acotadas a los grupos del contexto (requiere user_groups ya cargado)
          vueComponent.loadInProgressRecordings()
        }).catch((error) => {
          if (error.response && error.response.status === 400) {
            vueComponent.$noty.warning('La herramienta no ha recibido los datos necesarios. Habitualmente se resuelve configurando el navegador web para permitir Cookies de terceros / Seguimiento entre sitios.')
          }
        })
      }
    },
    getUserGroupsClassrooms () {
      let vueComponent = this
      this.data_loaded = true
      // EdF: La marca de carga se pone aquí y no solo en el arranque porque este método se llama
      // también desde cada filtro, desde las fechas y desde los refrescos, y lo primero que hace es
      // vaciar la lista. Sin ella, entre el vaciado y la respuesta el listado queda vacío y salta el
      // mensaje de "no hay sesiones", que solo debe verse cuando el filtro ya ha resuelto sin nada.
      this.loading = true
      this.user_classrooms = []
      this.recordings_loaded = false
      if (!this.user_groups.length > 0) {
        this.loading = false
        return
      }
      let filterParams = {
        groups: this.user_groups.map(g => g.unirid).join(',')
      }
      // CMM: Configuración de filtros
      if (vueComponent.filter_start_date) {
        filterParams.startTime = window.moment(vueComponent.filter_start_date).utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]')
        if (vueComponent.selected_filter === vueComponent.$i18n.t('sessions.interval')) {
          filterParams.startTime = window.moment(vueComponent.filter_start_date).startOf('day').utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]')
        }
      }
      if (vueComponent.filter_end_date) {
        filterParams.endTime = window.moment(vueComponent.filter_end_date).utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]')
        if (vueComponent.selected_filter === vueComponent.$i18n.t('sessions.interval')) {
          filterParams.endTime = window.moment(vueComponent.filter_end_date).endOf('day').utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]')
        }
      }
      filterParams.targetStatus = 1
      if (vueComponent.selected_filter === vueComponent.$i18n.t('sessions.showfilterfavorite')) {
        filterParams.favorite = true
      }
      if (vueComponent.selected_filter === vueComponent.$i18n.t('sessions.searchbyhost') && vueComponent.filter_host) {
        // EdF: Filtrado por impartidor. El select2 emite el unirId (proeduca) del usuario tipo 'P'
        filterParams.hostProeducaId = vueComponent.filter_host
      }

      window.axios.get('classrooms/', {params: filterParams}).then((response) => {
        // EdF: La lista se pinta completa y ya ordenada con esta primera respuesta, que trae todo lo que
        // la card necesita para mostrarse (fecha, duración, impartidor, sistema). Antes las clases se
        // añadían de una en una al resolver su matrícula y, como el listado ordena por fecha de inicio,
        // cada card se insertaba en medio y desplazaba a las de debajo. Los controles que dependen de la
        // matrícula ("Unirse") y de las grabaciones se completan después dentro de cada card, sin mover nada.
        const userGroupIds = vueComponent.user_groups.map(g => g.unirid)
        const enrollable = []
        response.data.results.forEach(function (classroom) {
          // CMM: Solo se manejan las clases en alguno de los grupos a los que pertenezca el usuario (user_groups)
          let anyGroup = vueComponent._.first(classroom.unirGroups.filter(unirGroupId => userGroupIds.includes(unirGroupId)))
          if (anyGroup) {
            enrollable.push({ anyGroup, classroom })
          }
        })
        vueComponent.user_classrooms = enrollable.map(e => e.classroom)
        vueComponent.loading = false

        // EdF: Cada cadena absorbe su propio error para que una matrícula que falle no impida que
        // aparezcan las filas de grabaciones CONNECT del resto de clases. Equivale a un allSettled,
        // que se evita porque core-js 2 (babel-runtime) no lo trae y aquí no hay polyfill.
        Promise.all(enrollable.map(e => vueComponent.enrollUserClassroom(e.anyGroup, e.classroom).catch(() => {})))
          .then(() => { vueComponent.recordings_loaded = true })
      }).catch(() => {
        // EdF: Si la consulta falla, el aviso lo da el interceptor de App.vue, pero hay que soltar la
        // marca de carga igualmente: si no, el listado se queda oculto y sin mensaje, en blanco.
        vueComponent.loading = false
      })
    },
    enrollUserClassroom (unirGroupId, classroom) {
      let vueComponent = this

      const userId = this.jwtLoggedUserId()
      const userLmsId = this.jwtLoggedUserLmsId()

      let enrollParams = {
        classroomId: classroom.id,
        unirGroup: unirGroupId,
        userId: userId,
        lmsUserId: userLmsId,
        roleCode: vueComponent.jwtLoggedUserTargetSystemRole(classroom.targetSystem)
      }

      return window.axios.post('classrooms/' + classroom.id + '/enrollments/', enrollParams).then((response) => {
        // EdF: $set y no asignación directa. La clase ya está en user_classrooms (y por tanto es reactiva)
        // antes de que llegue la matrícula, y en Vue 2 añadir una propiedad nueva a un objeto ya reactivo
        // no actualiza la vista: sin esto el botón "Unirse" no llegaría a aparecer nunca.
        vueComponent.$set(classroom, 'enrollment', response.data)
        // CMM/EdF: La llamada de grabaciones se mantiene encadenada aquí a propósito. Lanzarla sin que haya
        // respondido el POST de matrícula provoca enrollments duplicados en Connect.
        return vueComponent.getUserClassroomRecording(classroom)
      })
    },
    getUserClassroomRecording (classroom) {
      return window.axios.get('recordings/', { params: { classroomId: classroom.id } }).then((response) => {
        this.$set(classroom, 'recordings', response.data)
        this.$set(classroom, 'main_recording', this.getMainRecording(classroom.recordings, classroom.targetSystem))
        // EdF: Una sala CONNECT es una única clase reutilizada N veces, por lo que sus grabaciones
        // se listan como filas propias en lugar de condensarse en una main_recording
        this.$set(classroom, 'connect_recordings', classroom.targetSystem === 'CONNECT' ? (response.data || []) : [])
      })
    },
    getMainRecording (recordings, targetSystem) {
      if (targetSystem !== 'ZOOM' && targetSystem !== 'COLLAB') return null
      if (!recordings || recordings.length === 0) return null
      if (recordings.length === 1) return recordings[0]
      return recordings.reduce((a, b) => (b.duration > a.duration ? b : a))
    },
    handlePlayRecording ({ url, title, captionsUrl, chat, recording }) {
      this.player_url = url
      this.player_title = title
      this.player_captions_url = captionsUrl
      this.chat = chat
      this.current_recording = recording
      this.$bvModal.show('collaborate-recording-player-')
    },
    handleRecordingProgress (payload) {
      // Aplica el estado emitido por el reproductor a todas las copias en memoria de la misma
      // grabación (la card de "Seguir viendo" y la del listado son objetos distintos)
      const apply = (rec) => {
        if (!rec || String(rec.id) !== String(payload.recordingId)) return
        Object.keys(payload).forEach(k => {
          if (k !== 'recordingId') this.$set(rec, k, payload[k])
        })
      }
      this.in_progress_recordings.forEach(apply)
      this.user_classrooms.forEach(c => apply(c.main_recording))
      if (payload.playing) this.addToInProgress(payload.recordingId)
    },
    addToInProgress (recordingId) {
      // Al empezar a reproducir una grabación que aún no está en "Seguir viendo" la añadimos
      // en cliente: el PATCH de playing=true que ya lanza videoResumeMixin persiste el estado,
      // así que no hace falta releer recordings/in-progress. Si la lista estaba vacía el bloque
      // aparece solo, porque el v-if depende de in_progress_recordings.length.
      if (this.user_roles.indexOf('ROLE_PARTICIPANT') === -1) return
      if (this.in_progress_recordings.some(r => String(r.id) === String(recordingId))) return
      const recording = this.current_recording
      if (!recording || String(recording.id) !== String(recordingId)) return
      // Copia propia, como el resto de la lista: la card del carrusel y la del listado son
      // objetos distintos que handleRecordingProgress mantiene sincronizados.
      // Va al principio porque el carrusel ordena por última reproducción descendente, igual
      // que la query del backend (findInProgressByUserAndGroups, ORDER BY lastPlayedAt DESC).
      // El porcentaje se recalcula para que la barra entre con el progreso real en lugar de a 0
      // hasta el primer guardado de posición.
      const percentage = this.recordingPercentage(recording.lastPositionMs, recording.duration)
      this.in_progress_recordings.unshift({
        ...recording,
        percentage: percentage === null ? (recording.percentage || 0) : percentage
      })
      this.pending_recordings_count = this.in_progress_recordings.length
    },
    loadInProgressRecordings () {
      // EdF: "Seguir viendo" es exclusivo de estudiantes (ROLE_PARTICIPANT). Sin grupos del contexto tampoco hay
      // grabaciones en curso que mostrar. En ambos casos se evita la llamada y se deja la lista vacía.
      if (this.user_roles.indexOf('ROLE_PARTICIPANT') === -1 || !this.user_groups.length) {
        this.in_progress_recordings = []
        this.pending_recordings_count = 0
        return
      }
      const params = { groups: this.user_groups.map(g => g.unirid).join(',') }
      window.axios.get('recordings/in-progress', { params }).then((response) => {
        this.in_progress_recordings = response.data
        this.pending_recordings_count = response.data.length
      })
    },
    handleRemoveInProgress (recordingId) {
      // Se refleja el playing=false en la tarjeta del listado para ocultar el mensaje "Te quedan X min de grabación"
      const classroom = this.user_classrooms.find(c => c.main_recording && String(c.main_recording.id) === String(recordingId))
      if (classroom) classroom.main_recording.playing = false
      this.in_progress_recordings = this.in_progress_recordings.filter(r => r.id !== recordingId)
      this.pending_recordings_count = this.in_progress_recordings.length
    },
    handleViewedInProgress (recordingId) {
      const classroom = this.user_classrooms.find(c => c.main_recording && String(c.main_recording.id) === String(recordingId))
      if (classroom) classroom.main_recording.viewed = true
      this.handleRemoveInProgress(recordingId)
    },
    viewedSessions () {
      const hasFullDataset = this.data_loaded && (this.visual_filter !== null || this.selected_filter === this.$i18n.t('sessions.showfilterall'))
      this.selected_filter = this.$i18n.t('sessions.showfilterviewed')
      this.visual_filter = 'viewed'
      if (!hasFullDataset) {
        this.filter_start_date = null
        this.filter_end_date = null
        this.getUserGroupsClassrooms()
      }
    },
    notViewedSessions () {
      const hasFullDataset = this.data_loaded && (this.visual_filter !== null || this.selected_filter === this.$i18n.t('sessions.showfilterall'))
      this.selected_filter = this.$i18n.t('sessions.showfilternotviewed')
      this.visual_filter = 'not-viewed'
      if (!hasFullDataset) {
        this.filter_start_date = null
        this.filter_end_date = null
        this.getUserGroupsClassrooms()
      }
    },
    favoriteSessions () {
      this.selected_filter = this.$i18n.t('sessions.showfilterfavorite')
      this.visual_filter = null
      this.filter_start_date = null
      this.filter_end_date = null
      this.getUserGroupsClassrooms()
    },
    allSessions () {
      const hasFullDataset = this.data_loaded && (this.visual_filter !== null || this.selected_filter === this.$i18n.t('sessions.showfilterall'))
      this.selected_filter = this.$i18n.t('sessions.showfilterall')
      this.visual_filter = null
      if (!hasFullDataset) {
        this.filter_start_date = null
        this.filter_end_date = null
        this.getUserGroupsClassrooms()
      }
    },
    nextSessions () {
      let consumerCode = this.jwtConsumerCode()
      if (consumerCode != null && consumerCode === 'moodle') {
        window.document.querySelector('.body-content .full-width-content--small').classList.remove('interval-table-moodle')
      } else if (consumerCode != null && consumerCode === 'canvas') {
        window.document.querySelector('.body-content .full-width-content--small').classList.remove('interval-table-canvas')
      }
      this.selected_filter = this.$i18n.t('sessions.allnext')
      this.visual_filter = null
      let now = window.moment().utc()
      this.filter_start_date = now.format('YYYY-MM-DD[T]HH:mm:ss[Z]')
      now.add(14, 'days')
      this.filter_end_date = now.format('YYYY-MM-DD[T]HH:mm:ss[Z]')
      this.getUserGroupsClassrooms()
    },
    pastSessions () {
      let consumerCode = this.jwtConsumerCode()
      if (consumerCode != null && consumerCode === 'moodle') {
        window.document.querySelector('.body-content .full-width-content--small').classList.remove('interval-table-moodle')
      } else if (consumerCode != null && consumerCode === 'canvas') {
        window.document.querySelector('.body-content .full-width-content--small').classList.remove('interval-table-canvas')
      }
      this.selected_filter = this.$i18n.t('sessions.allpast')
      this.visual_filter = null
      this.filter_start_date = null
      this.filter_end_date = window.moment().utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]')
      this.getUserGroupsClassrooms()
    },
    intervalSessions () {
      this.selected_filter = this.$i18n.t('sessions.interval')
      this.visual_filter = null
      this.filter_start_date = window.moment().format('YYYY-MM-DD')
      this.filter_end_date = window.moment().format('YYYY-MM-DD')
      this.getUserGroupsClassrooms()
    },
    searchByHostSessions () {
      this.selected_filter = this.$i18n.t('sessions.searchbyhost')
      this.visual_filter = null
      this.filter_start_date = null
      this.filter_end_date = null
      this.filter_host = null
      // EdF: El listado se recarga al seleccionar un impartidor (@input del select2 -> getUserGroupsClassrooms)
    },
    launchUrl (url, target) {
      window.open(url, target)
    },
    getUnique (arr, comp) {
      const unique = arr.map(e => e[comp])
        .map((e, i, final) => final.indexOf(e) === i && i)
        .filter((e) => arr[e]).map(e => arr[e])
      return unique
    },
    confirmDelete (classroom) {
      let vueComponent = this
      this.$confirm(
        {
          title: `Eliminar sesión`,
          message: `Esta sesión se eliminará. ¿Estás seguro de que deseas continuar?`,
          button: {
            no: 'No, cancelar',
            yes: 'Si, eliminar'
          },
          callback: confirm => {
            if (confirm) {
              // CMM: Se elimina la sesión
              window.axios.delete('classrooms/' + classroom.id).then((response) => {
                if (response && response.status === 200) {
                  vueComponent.$noty.success('La sesión se ha eliminado correctamente', {
                    timeout: 3000,
                    progressBar: true
                  })
                  this.getUserGroupsClassrooms()
                }
              })
            }
          }
        }
      )
    },
    reuseClassroom (classroom) {
      this.reuse_classroom = classroom
      // Se abre la edicón de la sesión reutilizada
      this.$root.$emit('bv::toggle::collapse', 'sidebar-edit-reuse')
    },
    clearCache () {
      const licenceId = this.jwtConsumerId()
      const site = this.jwtContextSite()
      let vueComponent = this
      const popupMsg = vueComponent.$i18n.t('sessions.cachecleared')

      window.axios.post('licences/' + licenceId + '/site/' + site + '/cache').then((response) => {
        vueComponent.$noty.success(popupMsg, {
          timeout: 3000,
          progressBar: true
        })
      })
    },
    showIncidentPanel (classroom) {
      this.incident_classroom = classroom
      this.$bvModal.show('incident-panel')
    },
    sessionState (classroom) {
      if (this.isOngoing(classroom)) return 'in-progress'
      if (this.notYetStarted(classroom)) return 'not-started'
      if (this.hasEnded(classroom)) return 'finished'
      return null
    },
    classDuration (classroom) {
      const minutes = window.moment(classroom.end).diff(window.moment(classroom.start), 'minutes')
      return String(minutes).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Lti/Sessions.vue